import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import type {
  Conversation,
  Message,
  Applicant,
  DashboardStats,
  Interview,
  MessageTemplate,
  Workspace,
  WorkspaceMember,
  Form,
  FormField,
  FormSubmission,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';

export function useConversations(page: number = 1, limit: number = 50) {
  const { getAuthHeaders } = useAuthStore();
  
  return useQuery({
    queryKey: ['conversations', page, limit],
    queryFn: async (): Promise<{ conversations: Conversation[]; total: number }> => {
      const response = await fetch(
        `${API_URL}/api/messages/conversations?page=${page}&limit=${limit}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error('Failed to fetch conversations');
      return response.json();
    },
  });
}

export function useConversation(applicantId: string) {
  const { getAuthHeaders } = useAuthStore();
  
  return useQuery({
    queryKey: ['conversation', applicantId],
    queryFn: async (): Promise<{ applicant: Applicant; messages: Message[] }> => {
      const response = await fetch(
        `${API_URL}/api/messages/conversations/${applicantId}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error('Failed to fetch conversation');
      return response.json();
    },
    refetchInterval: 5000,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();
  
  return useMutation({
    mutationFn: async ({ 
      applicantId, 
      message, 
      templateId 
    }: { 
      applicantId: string; 
      message: string;
      templateId?: string;
    }) => {
      const response = await fetch(
        `${API_URL}/api/messages/conversations/${applicantId}/send`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ message, templateId }),
        }
      );
      if (!response.ok) throw new Error('Failed to send message');
      return response.json();
    },
    onSuccess: (_, { applicantId }) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', applicantId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useUpdateNotes() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();
  
  return useMutation({
    mutationFn: async ({ applicantId, notes }: { applicantId: string; notes: string }) => {
      const response = await fetch(
        `${API_URL}/api/messages/conversations/${applicantId}/notes`,
        {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes }),
        }
      );
      if (!response.ok) throw new Error('Failed to update notes');
      return response.json();
    },
    onSuccess: (_, { applicantId }) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', applicantId] });
    },
  });
}

export function useDashboardStats() {
  const { getAuthHeaders } = useAuthStore();
  
  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async (): Promise<DashboardStats> => {
      const response = await fetch(`${API_URL}/dashboard-api/stats`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      return response.json();
    },
    refetchInterval: 30000,
  });
}

export function useApplicants(search?: string, status?: string, page: number = 1) {
  const { getAuthHeaders } = useAuthStore();
  
  return useQuery({
    queryKey: ['applicants', search, status, page],
    queryFn: async (): Promise<{ applicants: Applicant[]; total: number }> => {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (status) params.set('status', status);
      params.set('page', page.toString());
      
      const response = await fetch(
        `${API_URL}/dashboard-api/applicants?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error('Failed to fetch applicants');
      return response.json();
    },
  });
}

export function useDeleteApplicant() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (applicantId: string) => {
      const response = await fetch(`${API_URL}/dashboard-api/applicants/${applicantId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to delete applicant');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}

export function useUpdateApplicantStatus() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();
  
  return useMutation({
    mutationFn: async ({ applicantId, status }: { applicantId: string; status: string }) => {
      const response = await fetch(
        `${API_URL}/api/messages/applicants/${applicantId}/status`,
        {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        }
      );
      if (!response.ok) throw new Error('Failed to update status');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}

export function useInterviews() {
  const { getAuthHeaders } = useAuthStore();
  
  return useQuery({
    queryKey: ['interviews'],
    queryFn: async (): Promise<{ interviews: Interview[] }> => {
      const response = await fetch(`${API_URL}/api/schedule/interviews`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch interviews');
      return response.json();
    },
  });
}

// Schedule / Interviews
export function useScheduleInterview() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      applicantId: string;
      dateTime: string;
      duration?: number;
      notes?: string;
    }) => {
      const response = await fetch(`${API_URL}/api/schedule/interview`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to schedule interview');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}

export function useAvailableSlots(date: string) {
  const { getAuthHeaders } = useAuthStore();

  return useQuery({
    queryKey: ['available-slots', date],
    queryFn: async (): Promise<{ slots: { start: string; end: string }[]; date: string; duration: number }> => {
      const response = await fetch(
        `${API_URL}/api/schedule/available-slots?date=${date}&duration=30`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error('Failed to fetch available slots');
      return response.json();
    },
    enabled: !!date,
  });
}

// Invite to Interview (sends SMS + Email + creates calendar event)
export function useInviteToInterview() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      applicantId: string;
      dateTime: string;
      duration?: number;
      notes?: string;
    }) => {
      const response = await fetch(`${API_URL}/api/schedule/invite`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to send interview invite');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}

// Send self-service booking link (no dateTime — applicant picks their own slot)
export function useSendBookingLink() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (applicantId: string) => {
      const response = await fetch(`${API_URL}/api/schedule/invite`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicantId }),
      });
      if (!response.ok) throw new Error('Failed to send booking link');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
    },
  });
}

export function useCancelInterview() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (applicantId: string) => {
      const response = await fetch(
        `${API_URL}/api/schedule/interview/${applicantId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        }
      );
      if (!response.ok) throw new Error('Failed to cancel interview');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}

// Templates
export function useTemplates() {
  const { getAuthHeaders } = useAuthStore();
  
  return useQuery({
    queryKey: ['templates'],
    queryFn: async (): Promise<{ templates: MessageTemplate[] }> => {
      const response = await fetch(`${API_URL}/api/templates`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch templates');
      return response.json();
    },
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();
  
  return useMutation({
    mutationFn: async (data: { name: string; content: string; variables?: string[] }) => {
      const response = await fetch(`${API_URL}/api/templates`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create template');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

// Workspace
export function useWorkspace() {
  const { getAuthHeaders } = useAuthStore();
  
  return useQuery({
    queryKey: ['workspace'],
    queryFn: async (): Promise<{ workspace: Workspace; members: WorkspaceMember[] }> => {
      const response = await fetch(`${API_URL}/workspaces/current`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch workspace');
      return response.json();
    },
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch(`${API_URL}/workspaces/current/logo`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to upload logo');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace'] });
    },
  });
}

export function useUpdateWorkspace() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      name?: string;
      companyName?: string;
      calendlyLink?: string;
      textfreeSid?: string;
      textfreeToken?: string;
      textfreePhone?: string;
      displayName?: string;
      formBackgroundColor?: string;
      primaryColor?: string;
      httpsmsApiKey?: string;
      httpsmsPhone?: string;
    }) => {
      const response = await fetch(`${API_URL}/workspaces/current`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update workspace');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace'] });
    },
  });
}

// Forms
export function useForms() {
  const { getAuthHeaders } = useAuthStore();

  return useQuery({
    queryKey: ['forms'],
    queryFn: async (): Promise<{ forms: Form[] }> => {
      const response = await fetch(`${API_URL}/api/forms`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch forms');
      return response.json();
    },
  });
}

export function useForm(id: string) {
  const { getAuthHeaders } = useAuthStore();

  return useQuery({
    queryKey: ['form', id],
    queryFn: async (): Promise<{ form: Form }> => {
      const response = await fetch(`${API_URL}/api/forms/${id}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch form');
      return response.json();
    },
    enabled: !!id,
  });
}

export function useCreateForm() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      description?: string;
      job_title: string;
      status: string;
      fields: FormField[];
      settings?: Record<string, string | boolean | undefined>;
    }) => {
      const response = await fetch(`${API_URL}/api/forms`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create form');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
    },
  });
}

export function useUpdateForm() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      description?: string;
      job_title?: string;
      status?: string;
      fields?: FormField[];
      settings?: Record<string, string | boolean | undefined>;
    }) => {
      const response = await fetch(`${API_URL}/api/forms/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update form');
      return response.json();
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      queryClient.invalidateQueries({ queryKey: ['form', id] });
    },
  });
}

export function useDeleteForm() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_URL}/api/forms/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to delete form');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
    },
  });
}

export function useUploadFormLogo() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch(`${API_URL}/api/forms/${id}/logo`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to upload form logo');
      return response.json();
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['form', id] });
    },
  });
}

export function useUploadFormBackground() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const formData = new FormData();
      formData.append('background', file);

      const response = await fetch(`${API_URL}/api/forms/${id}/background`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to upload form background');
      return response.json();
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['form', id] });
    },
  });
}

export function useFormSubmissions(formId: string) {
  const { getAuthHeaders } = useAuthStore();

  return useQuery({
    queryKey: ['form-submissions', formId],
    queryFn: async (): Promise<{ submissions: FormSubmission[] }> => {
      const response = await fetch(`${API_URL}/api/forms/${formId}/submissions`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch submissions');
      return response.json();
    },
    enabled: !!formId,
  });
}

// Onboarding
export function useOnboardingSetup() {
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { companyName: string; name: string }) => {
      const response = await fetch(`${API_URL}/workspaces/current`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to set up workspace');
      return response.json();
    },
  });
}

// Billing
export function useBillingStatus() {
  const { getAuthHeaders } = useAuthStore();

  return useQuery({
    queryKey: ['billing-status'],
    queryFn: async (): Promise<{
      plan: string;
      status: string;
      grandfathered: boolean;
      stripeCustomerId?: string | null;
      subscriptionId?: string | null;
      message?: string;
    }> => {
      const response = await fetch(`${API_URL}/api/billing`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch billing status');
      return response.json();
    },
  });
}

export function useCreateCheckout() {
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (): Promise<{ url: string }> => {
      const response = await fetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to create checkout');
      return response.json();
    },
  });
}

export function useBillingPortal() {
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (): Promise<{ url: string }> => {
      const response = await fetch(`${API_URL}/api/billing/portal`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to create portal session');
      return response.json();
    },
  });
}

// Email Sync
export function useSyncEmails() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/api/sync/emails`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to sync emails');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
    },
  });
}

// Email
export function useSendEmail() {
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      subject: string;
      body: string;
      applicantIds: string[];
    }) => {
      const response = await fetch(`${API_URL}/api/email/send`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to send email');
      return response.json();
    },
  });
}

export function useSendEmailAll() {
  const { getAuthHeaders } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      subject: string;
      body: string;
      statusFilter?: string;
    }) => {
      const response = await fetch(`${API_URL}/api/email/send-all`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to send email');
      return response.json();
    },
  });
}

// Resume
export function useResumeUrl(applicantId: string) {
  const { token } = useAuthStore();
  return `${API_URL}/api/resumes/${applicantId}?token=${token}`;
}

export function useUploadResume() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthStore();
  
  return useMutation({
    mutationFn: async ({ applicantId, file }: { applicantId: string; file: File }) => {
      const formData = new FormData();
      formData.append('resume', file);
      
      const response = await fetch(`${API_URL}/api/resumes/${applicantId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to upload resume');
      return response.json();
    },
    onSuccess: (_, { applicantId }) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', applicantId] });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
    },
  });
}
